<template>
  <div class="hcl-toolbar-content-action-select">
    <div class="hcl-toolbar-content-action-select-input-group input-group input-group-sm actionList">
      <Multiselect
        v-if="'actionselect' in bucketToolbar"
        class="hcl-toolbar-content-action-select-select"
        :options="bucketToolbar.actionselect.values"
        label="text"
        valueProp="id"
        v-model="selectId"
        :noOptionsText="props.definition.messages.entries_none"
        :placeholder="props.definition.messages.entries_placeholder"
        :searchable="true"
      >
      </Multiselect>
      <div class="input-group-append actionlist-buttons">
        <button
          v-if="showCompanies"
          type="button"
          class="btn btn-outline-default"
          :title="titleCompanies"
          @click="onCompaniesClick"
        >
          <i class="eii--fa6-solid--arrow-up-right-from-square"></i>
        </button>
        <button
          v-if="showApplicants"
          type="button"
          class="btn btn-outline-default"
          :title="titleApplicants"
          @click="onApplicantsClick"
        >
          <i class="eii--fa6-solid--user-check"></i>
        </button>
        <button
          v-if="showEmployees"
          type="button"
          class="btn btn-outline-default"
          :title="titleEmployees"
          @click="onEmployeesClick"
        >
          <i class="eii--fa6-solid--user-check"></i>
        </button>
        <button
          v-if="showContacts"
          type="button"
          class="btn btn-outline-default"
          :title="titleContacts"
          @click="onContactsClick"
        >
          <i class="eii--fa6-solid--user-check"></i>
        </button>
        <button
          v-if="showClear"
          type="button"
          class="btn btn-outline-default"
          :title="titleClear"
          @click="onClearClick"
        >
          <i class="eii--fa6-regular--trash-can"></i>
        </button>

        <button
          type="button"
          class="btn btn-outline-default dropdown-toggle"
          data-toggle="dropdown"
          aria-haspopup="true"
          aria-expanded="false"
          :title="titleButtonMenu"
        >
          <span class="sr-only">{{ titleButtonMenu }}</span>
          <i class="eii--fa6-solid--ellipsis-vertical"></i>
        </button>
        <div class="dropdown-menu dropdown-menu-right">
          <a
            class="dropdown-item"
            @click="modalAddIsOpen = !modalAddIsOpen"
          >{{ titleButtonMenuAdd }}</a>
          <a
            v-if="selectId"
            class="dropdown-item"
            @click="modalEditIsOpen = !modalEditIsOpen"
          >{{ titleButtonMenuEdit }}</a>
          <a
            v-if="selectId"
            class="dropdown-item"
            @click="modalDeleteIsOpen = !modalDeleteIsOpen"
          >{{ titleButtonMenuDelete }}</a>
          <div
            v-if="selectedValue.actions?.length > 0"
            class="dropdown-divider"
          ></div>
          <a
            class="dropdown-item"
            v-for="action of selectedValue.actions"
            key="action.id"
            :href="action.url"
          >
            {{ action.text }}
          </a>
        </div>
      </div>
    </div>
    <modal-add
      :open="modalAddIsOpen"
      :messages="props.definition.messages"
      :urls="props.definition.urls"
      :bucket="bucketToolbar"
      :selected-value="selectedValue"
      :typeId="props.definition.typeId"
      @modal-close="onModalAddClose"
    >
    </modal-add>
    <modal-edit
      :open="modalEditIsOpen"
      :messages="props.definition.messages"
      :urls="props.definition.urls"
      :bucket="bucketToolbar"
      :selected-value="selectedValue"
      :typeId="props.definition.typeId"
      @modal-close="onModalEditClose"
    >
    </modal-edit>
    <modal-delete
      :open="modalDeleteIsOpen"
      :messages="props.definition.messages"
      :urls="props.definition.urls"
      :bucket="bucketToolbar"
      :selected-value="selectedValue"
      @modal-close="onModalDeleteClose"
    >
    </modal-delete>
    <modal-clear
      :open="modalClearIsOpen"
      :messages="props.definition.messages"
      :urls="props.definition.urls"
      :bucket="bucketToolbar"
      :selected-value="selectedValue"
      @modal-close="onModalClearClose"
    >
    </modal-clear>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, watch, inject } from 'vue'

import type { Ref } from 'vue'

import type {
  DataActionSelect,
  DefinitionListContentConfiguration,
  DefinitionToolbarContentActionSelect
} from '@/components/types'

import { useDataStore } from '@/stores/data'

import { useActionLists } from './actionlists'

import ModalAdd from './ModalAdd.vue'
import ModalEdit from './ModalEdit.vue'
import ModalDelete from './ModalDelete.vue'
import ModalClear from './ModalClear.vue'

import Multiselect from '@vueform/multiselect'

interface Props {
  /**
   * Toolbar Content Action Select definition.
   */
  definition: DefinitionToolbarContentActionSelect
  /**
   * Configuration.
   */
  config?: DefinitionListContentConfiguration
}

const props = withDefaults(defineProps<Props>(), {
  definition: undefined,
  config: undefined
})

const toolbarMetaId: string = inject('toolbarMetaId', '')
const blockMetaId: string = inject('blockMetaId', '')

const { link } = useDataStore()

const bucketToolbar: Ref<DataActionSelect> = link(toolbarMetaId)

const listMetaId: string = inject('listMetaId', '')

const {
  changeActionList,
  addCompaniesToActionList,
  addContactsToActionList,
  addApplicantsToActionList,
  addEmployeesToActionList,
  clearActionList
} = useActionLists()

const selectId: Ref<string | null> = ref(null)
const uuid: Ref<string | undefined> = ref(undefined)

if ('actionselect' in bucketToolbar.value && 'id' in bucketToolbar.value.actionselect) {
  selectId.value = bucketToolbar.value.actionselect.id === '0' ? null : bucketToolbar.value.actionselect.id
}

if ('actionselect' in bucketToolbar.value && 'uuid' in bucketToolbar.value.actionselect) {
  uuid.value = bucketToolbar.value.actionselect.uuid
}

const selectIdNotEmpty = computed(() => {
  return selectId.value !== null && selectId.value !== '0'
})

const showCompanies = computed(() => {
  return bucketToolbar.value.actionselect?.selections?.includes('companies') && selectIdNotEmpty.value
})

const titleCompanies = computed(() => {
  return props.definition.messages.companies_add ?? ''
})

const showApplicants = computed(() => {
  return bucketToolbar.value.actionselect?.selections?.includes('applicants') && selectIdNotEmpty.value
})

const titleApplicants = computed(() => {
  return props.definition.messages.applicants_add ?? ''
})

const showEmployees = computed(() => {
  return bucketToolbar.value.actionselect?.selections?.includes('employees') && selectIdNotEmpty.value
})

const titleEmployees = computed(() => {
  return props.definition.messages.employees_add ?? ''
})

const showContacts = computed(() => {
  return bucketToolbar.value.actionselect?.selections?.includes('contacts') && selectIdNotEmpty.value
})

const titleContacts = computed(() => {
  return props.definition.messages.contacts_add ?? ''
})

const showClear = computed(() => {
  return selectIdNotEmpty.value
})

const titleClear = computed(() => {
  return props.definition.messages.clear_list ?? ''
})

const titleButtonMenu = computed(() => {
  return props.definition.messages.button_menu ?? ''
})

const titleButtonMenuAdd = computed(() => {
  return props.definition.messages.modals.list_add.title ?? ''
})

const titleButtonMenuEdit = computed(() => {
  return props.definition.messages.modals.list_edit.title ?? ''
})

const titleButtonMenuDelete = computed(() => {
  return props.definition.messages.modals.list_delete.title ?? ''
})

const selectedValue = computed(() => {
  const filtered = bucketToolbar.value.actionselect.values.filter(value => { return value.id === selectId.value })
  return filtered.length > 0 ? filtered[0] : { id: '', type: '', type_id: '', text: '', actions: [] }
})

/*
 * Primary Entities
 */
const onCompaniesClick = () => {
  addCompaniesToActionList({
    actionListId: selectId.value,
    listMetaId: listMetaId,
    selectionColumName: props.config?.rows.columns.selection.column,
    blockMetaId: blockMetaId,
    url_list_apply_all: props.definition.urls.list_apply_all
  })
}

const onApplicantsClick = () => {
  addApplicantsToActionList({
    actionListId: selectId.value,
    listMetaId: listMetaId,
    selectionColumName: props.config?.rows.columns.selection.column,
    blockMetaId: blockMetaId,
    url_list_apply_all: props.definition.urls.list_apply_all
  })
}

const onEmployeesClick = () => {
  addEmployeesToActionList({
    actionListId: selectId.value,
    listMetaId: listMetaId,
    selectionColumName: props.config?.rows.columns.selection.column,
    blockMetaId: blockMetaId,
    url_list_apply_all: props.definition.urls.list_apply_all
  })
}

/*
 * Secondary Entities
 */
const onContactsClick = () => {
  addContactsToActionList({
    actionListId: selectId.value,
    listMetaId: listMetaId,
    selectionColumName: props.config?.rows.columns.selection.column,
    blockMetaId: blockMetaId,
    url_list_apply_subentity_all: props.definition.urls.list_apply_subentity_all
  })
}

const onClearClick = () => {
  modalClearIsOpen.value = true
}

watch(selectId, (n) => {
  if (n !== null) {
    selectedValue.value.actions = selectedValue.value.actions.map((action) => {
      action.url = action.url.replace(/\d+$/, n)
      return action
    })
  }

  changeActionList(
    {
      actionListId: n ? n : '0',
      listMetaId: listMetaId,
      selectionColumName: props.config?.rows?.columns?.selection?.column,
      blockMetaId: blockMetaId,
      url_list_change: props.definition.urls.list_change
    }
  )
})

/*
 * Modal Handling
 */
const modalAddIsOpen = ref(false)
const modalEditIsOpen = ref(false)
const modalDeleteIsOpen = ref(false)
const modalClearIsOpen = ref(false)

const onModalAddClose = (event: any) => {
  modalAddIsOpen.value = false

  if (event.action !== 'save') return

  if (event.payload.success === true) {
    if (event.payload.list.type_id === props.definition.typeId) {
      bucketToolbar.value.actionselect.values.push(event.payload.list)
      selectId.value = event.payload.list.id
    }
  }
}

const onModalEditClose = (event: any) => {
  modalEditIsOpen.value = false

  if (event.action !== 'save') return

  if (event.payload.success === true) {
    const index = bucketToolbar.value.actionselect.values.findIndex((option) => option.id === selectId.value)

    if (index > -1) {
      if (bucketToolbar.value.actionselect.values[index].type_id === event.payload.list.type_id) {
        bucketToolbar.value.actionselect.values[index].type = event.payload.list.type
        bucketToolbar.value.actionselect.values[index].type_id = event.payload.list.type_id
        bucketToolbar.value.actionselect.values[index].text = event.payload.list.text
        bucketToolbar.value.actionselect.values[index].actions = event.payload.list.actions
      } else {
        bucketToolbar.value.actionselect.values.splice(index, 1)
      }
    }
  }
}

const onModalDeleteClose = (event: any) => {
  modalDeleteIsOpen.value = false

  if (event.action !== 'yes') return

  if (event.payload.success === true) {
    const index = bucketToolbar.value.actionselect.values.findIndex((option) => option.id === selectId.value)

    if (index >= 0) {
      bucketToolbar.value.actionselect.values.splice(index, 1)
      selectId.value = null
    }
  }
}

const onModalClearClose = (event: any) => {
  modalClearIsOpen.value = false

  if (event.action !== 'yes' || !listMetaId) return

  clearActionList({
    actionListId: selectId.value,
    listMetaId: listMetaId,
    selectionColumName: props.config?.rows.columns.selection.column,
    blockMetaId: blockMetaId,
    url_list_empty_all: props.definition.urls.list_empty_all
  })
}
</script>

<style lang="scss" scoped>
.hcl-toolbar-content-action-select {
  display: inline-flex;
  vertical-align: middle;
  flex-grow: 1;

  .hcl-toolbar-content-action-select-select {
    width: 100%;
    min-width: 14em;
  }

  .hcl-toolbar-content-action-select-input-group {
    flex-wrap: nowrap;

    .actionlist-buttons {
      button {
        min-width: 1.75rem;
      }
    }
  }
}
</style>
